/* eslint-disable-off @typescript-eslint/ban-types */
/* eslint-disable-off functional/no-return-void */
/*
   Table Paginated & User Customizable
*/
import React, { useCallback, useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  Cell,
  Column,
  Row,
  SortingRule,
  usePagination,
  useResizeColumns,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';

import { GridFooter } from '../..';
import ResizableTable from '../resizable-table/resizable-table.js';
import './table-paginated-uc.css';

const defTableProps = {
  striped: true,
  hover: true,
  bordered: true,
  className: 'mb-0',
};
const defHeaderGroupProps = () => ({ className: 'text-center' });
const defHeaderProps = () => ({ className: 'align-middle' });
const defFooterGroupProps = () => ({ className: 'text-center' });

// eslint-disable-off-next-line-off functional/no-mixed-type
type Props<D extends object = {}> = {
  readonly id: string; // for storing to localstorage
  readonly appStore: any;
  readonly title?: string;
  readonly columns: ReadonlyArray<Column<D>>;
  readonly data: readonly D[];
  readonly dataCount: number;
  readonly hidePagination?: boolean;
  readonly initPageIndex?: number;
  readonly hasInsert?: boolean;
  readonly tableProps?: object;
  readonly bodyProps?: object;
  readonly headerGroupProps?: (headerGroup) => object;
  readonly footerGroupProps?: (footerGroup) => object;
  readonly headerProps?: (column: Column<D>) => object;
  readonly rowProps?: (row: Row<D>) => object;
  readonly cellProps?: (cell: Cell<D>) => object;
  readonly onSelectedRowsChange?: (rows: ReadonlyArray<Row<D>>) => void;
  readonly onPageChange?: (pageIndex, pageSize: number) => void;
  readonly onSortByChange?: (sort: readonly SortingRule<D>[]) => void;
  readonly onExport?: () => void;
  readonly resizable?: boolean;
  readonly selectedRowIndex?: number;
  readonly style?: any;
  readonly onRowClick?: (tableInstance: unknown) => void;
  hasFooter?: boolean;
};

// eslint-disable-off-next-line-off @typescript-eslint/explicit-module-boundary-types
export function TablePaginatedUC<D extends object = {}>({
  id,
  appStore,
  title = '',
  columns,
  data,
  dataCount,
  hidePagination = false,
  initPageIndex = 0,
  hasInsert = false,
  onSelectedRowsChange = null,
  onPageChange = null,
  onSortByChange = null,
  onExport,
  tableProps = defTableProps,
  headerGroupProps = defHeaderGroupProps,
  footerGroupProps = defFooterGroupProps,
  headerProps = defHeaderProps,
  bodyProps = null,
  rowProps = null,
  cellProps = null,
  resizable = false,
  style = {},
  selectedRowIndex = null,
  onRowClick = null,
  hasFooter = false,
}: Props<D>) {
  //
  //-------------------- Body -----------------------------------------------
  //
  const { i18n, t } = useTranslation();
  const controlledPageCount = ~~((dataCount - 1) / appStore.pageSize) + 1;

  // Add Customizable Column [Toggle Visible Columns] ----------------------------------------
  const [custMenuIsOpen, setCustMenuIsOpen] = useState(false);
  const customizeMenuComp = () => {
    return (
      <Dropdown
        className={!custMenuIsOpen ? 'show-onhover' : null}
        onToggle={(isOpen) => {
          setCustMenuIsOpen(isOpen);
          if (!isOpen) {
            // Store Columns Locally
            // Get Visible Columns
            const store = allColumns
              //.filter(col => col.id !== 'customizeMenu')
              .map((col) => {
                return {
                  id: col.id,
                  isVisible: col.isVisible,
                  width: col.width,
                };
              });
            // Store Columns Locally
            localStorage.setItem('columns-' + id, JSON.stringify(store));
          }
        }}
      >
        <Dropdown.Toggle variant="primary" size="sm" id="tableuc-dropdown" />
        <Dropdown.Menu
          style={{
            paddingLeft: '0.6rem',
            minWidth: '20rem',
            fontSize: '1rem',
            fontWeight: 'normal',
          }}
        >
          <Dropdown.Header>{t('Select the visible columns')}</Dropdown.Header>
          {allColumns
            // .filter((column, index) => index > 0)
            .map((column) => (
              <div key={column.id}>
                <label>
                  <input type="checkbox" {...column.getToggleHiddenProps()} />{' '}
                  {column.Header as any}
                </label>
              </div>
            ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  // ...........................................................................
  useEffect(() => {
    // Read Local Storage and set visible columns
    const lcColumnsStr = localStorage.getItem('columns-' + id);
    if (lcColumnsStr !== null) {
      const lcColumns = JSON.parse(lcColumnsStr);
      lcColumns.forEach((lcCol) => {
        const col = allColumns.find((element) => element.id === lcCol.id);
        if (col) {
          col.toggleHidden(!lcCol.isVisible); // hide
        }
      });
    }

    // eslint-disable-off-next-line-off react-hooks/exhaustive-deps
  }, []);
  // --------------------------------------------------------------------------

  const defaultColumn = React.useMemo(
    () => ({
      width: 150,
    }),
    []
  );
  /**
   * * useTable : Main Table Machine
   */
  const tableInstance = useTable<D>(
    {
      columns,
      data,
      initialState: { pageIndex: initPageIndex, pageSize: appStore.pageSize }, // Pass our hoisted table state
      manualPagination: true, // Enable Manual Pagination
      manualSortBy: true, // Enable Manual Sorting
      disableMultiSort: true, // Disable Miltiple Sorting
      pageCount: controlledPageCount,
      defaultColumn,
    },
    useSortBy,
    usePagination,
    useRowSelect,
    useResizeColumns
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    prepareRow,
    page,
    pageCount,
    gotoPage,
    setPageSize,
    selectedFlatRows,
    state: { pageIndex, pageSize, sortBy },
    allColumns,
    visibleColumns,
    toggleRowSelected,
    rows,
  } = tableInstance;

  /* *** Effect για επιλογή γραμμής *** */
  useEffect(() => {
    if (selectedRowIndex !== null) {
      toggleRowSelected(rows[selectedRowIndex].id, true);
    }
  }, [rows, selectedRowIndex, toggleRowSelected]);

  /**
   * * 2. Effect : ΕΚΤΕΛΕΙ ΤΟ gotoPage οταν Αλλάξει το initPageIndex
   */
  useEffect(() => {
    gotoPage(initPageIndex);
  }, [gotoPage, initPageIndex]);

  /**
   * * 3. Effect : ΕΚΤΕΛΕΙΤΑΙ όταν αλλάξει το selectedFlatRows
   */
  useEffect(() => {
    onSelectedRowsChange && onSelectedRowsChange(selectedFlatRows);
  }, [onSelectedRowsChange, selectedFlatRows]);

  /**
   * * 4. Effect : ΕΚΤΕΛΕΙΤΑΙ όταν αλλάξει το sortBy
   */
  useEffect(() => {
    onSortByChange && onSortByChange(sortBy);
  }, [onSortByChange, sortBy]);

  //
  //-------------------- Handlers -------------------------------------------
  //
  const handlePageChange = useCallback(
    (pageNo) => {
      if (onPageChange != null) {
        onPageChange(pageNo, pageSize);
      }
      gotoPage(pageNo);
    },
    [gotoPage, onPageChange, pageSize]
  );

  const handlePageSizeChange = useCallback(
    (n: number) => {
      appStore.setPageSize(n);
      setPageSize(n);
      gotoPage(0);
      if (onPageChange != null) onPageChange(0, n);
    },
    [gotoPage, onPageChange, appStore, setPageSize]
  );
  //
  //-------------------- Render ----------------------------------------------
  //
  // useWhyDidYouUpdate('[TablePaginated]', {
  //   columns,
  //   data,
  //   dataCount,
  //   get,
  //   initPageIndex,
  //   gotoPage,
  //   onPageChange,
  //   onSelectedRowsChange,
  //   tableProps,
  //   pageIndex,
  //   pageSize,
  //   setPageSize,
  //   selectedFlatRows,
  //   title,
  //   variables,
  //   where,
  // });

  // const [widths, setWidths] = useState([]);
  // useEffect(() => {
  //   setWidths(columns.map((v) => v.width));
  // }, [columns]);

  /* const [widths, setWidths] = useState(
    columns.map((v) =>
      v.width ? Number(v.width.replace(/px/, '').replace(/%/, '')).valueOf() : 5
    )
  ); */
  const [widths, setWidths] = useState([35]);

  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ ...style, minWidth: '1024px' }}>
        <ResizableTable
          id={id}
          resizable={resizable}
          resizeOptions={{
            widths,
            onResize: (e) => {
              // // Read Widths from localstorage
              // const w = localStorage[id].split(';');
              // // Inform columns and resave to localstorage
              // // Get Visible Columns
              // const store = allColumns.map((col, index) => {
              //   return {
              //     id: col.id,
              //     isVisible: col.isVisible,
              //     width: col.isVisible ? w[index] : col.width,
              //   };
              // });
              // // Store Columns Locally
              // //localStorage.setItem('columns-' + id, JSON.stringify(store));

              //
              console.log('[table-paginated-uc] onResize', id);
            },
            minWidth: 15, //px
            resizeMode: 'fit', //mode can be 'fit', 'flex' or 'overflow'
          }}
          {...getTableProps(tableProps || {})}
        >
          <thead className="thead-dark hover-space">
            {headerGroups.map((headerGroup) => (
              <tr
                {...headerGroup.getHeaderGroupProps(
                  (headerGroupProps && headerGroupProps(headerGroup)) || {}
                )}
              >
                {headerGroup.headers.map((column, index) => {
                  if (index === 0) {
                    return (
                      <th
                        className="show-onhover"
                        {...column.getHeaderProps(
                          (headerProps && headerProps(column)) || {}
                        )}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'baseline',
                          }}
                        >
                          <span style={{ marginRight: '6px' }}>
                            {customizeMenuComp()}
                          </span>
                          <span>{column.render('Header')}</span>
                        </div>
                      </th>
                    );
                  } else
                    return (
                      <th
                        {...column.getHeaderProps(
                          (headerProps && headerProps(column)) || {}
                        )}
                      >
                        <span {...column.getSortByToggleProps()}>
                          {column.render('Header')}
                          {/* Add a sort direction indicator */}
                          {column.isSorted
                            ? column.isSortedDesc
                              ? ' 🔽'
                              : ' 🔼'
                            : ''}
                        </span>
                      </th>
                    );
                })}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps(bodyProps || {})}>
            {page.map((row) => {
              prepareRow(row);

              return (
                <tr
                  {...row.getRowProps((rowProps && rowProps(row)) || {})}
                  onClick={() => {
                    if (onRowClick) onRowClick({ ...tableInstance, row });
                  }}
                >
                  {row.cells.map((cell) => {
                    /* width on columns */
                    const lcWidthProps =
                      typeof cell?.column?.width === 'string' &&
                      cell.column.width
                        ? { width: cell.column.width }
                        : {};

                    const lcCellProps = cell.getCellProps(
                      (cellProps && cellProps(cell)) || {}
                    );
                    const lcProps = { ...lcWidthProps, ...lcCellProps };
                    //console.log('$$$', typeof cell?.column?.width, cell?.column?.width, lcProps);

                    return <td {...lcProps}>{cell.render('Cell')}</td>;
                    //return <td {...cell.getCellProps((cellProps && cellProps(cell)) || {})}>{cell.render('Cell')}</td>;
                  })}
                </tr>
              );
            })}
          </tbody>
          {hasFooter && (
            <tfoot style={{ backgroundColor: '#e1f3f7' }}>
              {footerGroups.map((footerGroup) => (
                <tr
                  {...footerGroup.getFooterGroupProps(
                    (footerGroupProps && footerGroupProps(footerGroup)) || {}
                  )}
                >
                  {footerGroup.headers.map((column) => (
                    <td {...column.getFooterProps()}>
                      {column.render('Footer')}
                    </td>
                  ))}
                </tr>
              ))}
            </tfoot>
          )}
        </ResizableTable>
        <GridFooter
          pageCount={pageCount}
          pageIndex={pageIndex}
          pageSize={pageSize}
          recordCount={dataCount}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onExport={onExport}
          hidePagination={hidePagination}
        />
      </div>
    </div>
  );
}
